import { FormState, useWatch } from '@vtex/admin-ui-form'
import { useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { FieldGroupBox, RuleStack } from 'ui'
import { RegularScope } from '../../../../../../typings'
import { SCOPE_OPTIONS } from '../../../../../ItemsView/typing'
import { EMPTY_SCOPE_RESTRICTION } from '../constants'
import { AddChip } from './AddChip'
import { EmptyChip } from './EmptyChip'
import { SELECTOR_MAP, SelectorComponentProps } from './constants'
import { messages } from './messages'
import { styles } from './styles'

type Props = {
  name: string
  form: FormState
  selectedOption: SCOPE_OPTIONS | undefined
}

function SelectorComponent(props: SelectorComponentProps) {
  const { name } = props

  if (!name) return null

  const Component = SELECTOR_MAP[name]

  if (!Component) return null

  return <Component {...(props as any)} />
}

export function ItemsEditor(props: Props) {
  const { name, form, selectedOption: selectedOptionFromView } = props

  const [selectedOption, setSelectedOption] = useState(selectedOptionFromView)

  // the useWatch support name as undefined although is not exposed by admin-ui-form
  const scope: RegularScope = useWatch({
    form,
    name: undefined as unknown as string,
  })

  const isEmpty = useMemo(
    () =>
      Object.values(SCOPE_OPTIONS).every(
        (option) => scope[option] === undefined
      ),
    [scope]
  )

  const addScopeRestriction = (option: SCOPE_OPTIONS) => {
    setSelectedOption(option)
    form.setValue(option, {
      ...EMPTY_SCOPE_RESTRICTION,
    })
  }

  if (isEmpty) {
    return (
      <FieldGroupBox
        name={name}
        state={form}
        withBorder={false}
        className={styles.emptyItems}
      >
        {Object.values(SCOPE_OPTIONS).map((option) => (
          <FieldGroupBox
            name={option}
            state={form}
            key={option}
            withBorder={false}
          >
            <EmptyChip
              name={option}
              form={form}
              option={option}
              onClick={() => addScopeRestriction(option)}
            />
          </FieldGroupBox>
        ))}
      </FieldGroupBox>
    )
  }

  return (
    <>
      <div className={styles.addChipContainer}>
        {Object.values(SCOPE_OPTIONS)
          .filter((name) => !scope[name])
          .map((name) => (
            <AddChip
              name={name}
              key={name}
              onClick={() => addScopeRestriction(name)}
            >
              <FormattedMessage {...messages[name]} />
            </AddChip>
          ))}
      </div>
      <FieldGroupBox
        name={name}
        state={form}
        withBorder={false}
        className={styles.items}
      >
        <RuleStack>
          {Object.values(SCOPE_OPTIONS)
            .filter((name) => scope[name])
            .map((name) => (
              <SelectorComponent
                key={name}
                name={name}
                form={form}
                open={name === selectedOption}
              />
            ))}
        </RuleStack>
      </FieldGroupBox>
    </>
  )
}
